import React, { useState } from "react";
import "./Gallery.css";
import Icon from "../Icon";
import { faArrowsLeftRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import SEO from "../SEO";

const officePhotos = [
  {
    id: 1,
    src: "/imgs/gallery/office/office-exterior.webp",
    alt: "Benson Dental Studio exterior in Bensonhurst Brooklyn",
    caption: "Our Bensonhurst location on Benson Ave",
  },
  {
    id: 2,
    src: "/imgs/gallery/office/waiting-area.webp",
    alt: "Modern waiting area at Benson Dental Studio Brooklyn",
    caption: "Comfortable, modern waiting area",
  },
  {
    id: 3,
    src: "/imgs/gallery/office/operatory.webp",
    alt: "State-of-the-art dental operatory at Benson Dental Studio",
    caption: "State-of-the-art operatory",
  },
  {
    id: 4,
    src: "/imgs/gallery/office/cbct.webp",
    alt: "CBCT 3D imaging scanner at Benson Dental Studio Brooklyn",
    caption: "CBCT 3D imaging for precise diagnostics",
  },
  {
    id: 5,
    src: "/imgs/gallery/office/intraoral-scanner.webp",
    alt: "Digital intraoral scanner at Benson Dental Studio",
    caption: "Digital intraoral scanner, no messy impressions",
  },
  {
    id: 6,
    src: "/imgs/gallery/office/sterilization.webp",
    alt: "Sterilization center at Benson Dental Studio Brooklyn NY",
    caption: "Dedicated sterilization center",
  },
];

const galleryItems = [
  {
    id: 1,
    category: "Teeth Whitening",
    title: "Professional Whitening Treatment",
    description:
      "Patient achieved 6 shades whiter smile with our in-office whitening treatment.",
    beforeImage: "/imgs/gallery/whitening-before.jpg",
    afterImage: "/imgs/gallery/whitening-after.jpg",
  },
  {
    id: 2,
    category: "Veneers",
    title: "Porcelain Veneer Smile Makeover",
    description:
      "Complete smile transformation with custom porcelain veneers to correct spacing and discoloration.",
    beforeImage: "/imgs/gallery/veneers-before.jpg",
    afterImage: "/imgs/gallery/veneers-after.jpg",
  },
  {
    id: 3,
    category: "Dental Crowns",
    title: "Crown Restoration",
    description:
      "Damaged tooth restored with a natural-looking porcelain crown.",
    beforeImage: "/imgs/gallery/crown-before.jpg",
    afterImage: "/imgs/gallery/crown-after.jpg",
  },
  {
    id: 4,
    category: "Dental Bonding",
    title: "Chipped Tooth Repair",
    description:
      "Front tooth chip repaired with tooth-colored composite bonding for a seamless result.",
    beforeImage: "/imgs/gallery/bonding-before.jpg",
    afterImage: "/imgs/gallery/bonding-after.jpg",
  },
  {
    id: 5,
    category: "Full Mouth",
    title: "Full Mouth Reconstruction",
    description:
      "Comprehensive restoration combining crowns, veneers, and whitening for a complete smile transformation.",
    beforeImage: "/imgs/gallery/fullmouth-before.jpg",
    afterImage: "/imgs/gallery/fullmouth-after.jpg",
  },
  {
    id: 6,
    category: "Dental Implants",
    title: "Single Tooth Implant",
    description:
      "Missing tooth replaced with a dental implant and crown for a permanent, natural-looking solution.",
    beforeImage: "/imgs/gallery/implant-before.jpg",
    afterImage: "/imgs/gallery/implant-after.jpg",
  },
];

const categories = ["All", ...new Set(galleryItems.map((item) => item.category))];

const GalleryCard = ({ item }) => {
  const [showAfter, setShowAfter] = useState(false);

  return (
    <article className="gallery-card">
      <div className="gallery-image-container">
        <div
          className="gallery-image-wrapper"
          onMouseEnter={() => setShowAfter(true)}
          onMouseLeave={() => setShowAfter(false)}
          onFocus={() => setShowAfter(true)}
          onBlur={() => setShowAfter(false)}
          tabIndex={0}
          role="img"
          aria-label={`${item.title}: ${showAfter ? "After" : "Before"} treatment`}
        >
          <img
            src={showAfter ? item.afterImage : item.beforeImage}
            alt={`${item.title} - ${showAfter ? "After" : "Before"}`}
            className="gallery-image"
            loading="lazy"
          />
          <div className="gallery-image-label">
            <span className={`label ${showAfter ? "after" : "before"}`}>
              {showAfter ? "After" : "Before"}
            </span>
          </div>
          <div className="gallery-hover-hint">
            <Icon icon={faArrowsLeftRight} />
            <span>Hover to see results</span>
          </div>
        </div>
      </div>
      <div className="gallery-card-content">
        <span className="gallery-category">{item.category}</span>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
      </div>
    </article>
  );
};

const OfficePhotoCard = ({ photo, onClick }) => (
  <article className="office-photo-card" onClick={() => onClick(photo)}>
    <div className="office-photo-image-container">
      <img
        src={photo.src}
        alt={photo.alt}
        className="office-photo-image"
        loading="lazy"
      />
    </div>
    <p className="office-photo-caption">{photo.caption}</p>
  </article>
);

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const filteredItems =
    activeCategory === "All"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeCategory);

  return (
    <main className="center-container fade-in">
      <SEO
        title="Gallery | Our Office & Smile Transformations"
        description="Tour our modern dental office in Bensonhurst, Brooklyn featuring CBCT 3D imaging, digital scanners, and state-of-the-art operatories. View before and after photos of teeth whitening, veneers, crowns, and full mouth reconstructions."
        canonical="/gallery"
        noindex
      />
      <div className="gallery-container">
        <header className="gallery-header">
          <h1>Gallery</h1>
          <p>
            Tour our modern office and see the smile transformations we've
            achieved for our patients.
          </p>
          <nav className="gallery-jump-links" aria-label="Jump to section">
            <a href="#office-technology">Our Office</a>
            <a href="#smile-transformations">Smile Transformations</a>
          </nav>
        </header>

        {/* Office & Technology Section */}
        <section id="office-technology" className="gallery-section">
          <h2>Our Office &amp; Technology</h2>
          <p className="gallery-section-subtitle">
            Our Bensonhurst office features the latest dental technology for
            precise, comfortable care.
          </p>
          <div className="office-photo-grid">
            {officePhotos.map((photo) => (
              <OfficePhotoCard
                key={photo.id}
                photo={photo}
                onClick={setLightboxPhoto}
              />
            ))}
          </div>
        </section>

        {/* Smile Transformations Section */}
        <section id="smile-transformations" className="gallery-section">
          <h2>Smile Transformations</h2>
          <p className="gallery-section-subtitle">
            Hover over each image to see the results.
          </p>

          <nav className="gallery-filters" aria-label="Gallery category filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-button ${
                  activeCategory === category ? "active" : ""
                }`}
                onClick={() => setActiveCategory(category)}
                aria-pressed={activeCategory === category}
              >
                {category}
              </button>
            ))}
          </nav>

          <div className="gallery-grid">
            {filteredItems.map((item) => (
              <GalleryCard key={item.id} item={item} />
            ))}
          </div>

          <div className="gallery-disclaimer">
            <p>
              <strong>Note:</strong> Results may vary. These images represent
              actual patient outcomes at Benson Dental Studio. All patients have
              provided consent for their photos to be shared.
            </p>
          </div>
        </section>

        <section className="gallery-cta">
          <h2>Ready for Your Transformation?</h2>
          <p>
            Schedule a consultation to discuss how we can help you achieve your
            perfect smile.
          </p>
          <a href="tel:718-266-5889" className="cta-button">
            Call 718-266-5889
          </a>
        </section>
      </div>

      {/* Lightbox Modal */}
      {lightboxPhoto && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxPhoto(null)}
          role="dialog"
          aria-label={lightboxPhoto.caption}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setLightboxPhoto(null)}
              aria-label="Close lightbox"
            >
              <Icon icon={faXmark} />
            </button>
            <img
              src={lightboxPhoto.src}
              alt={lightboxPhoto.alt}
              className="lightbox-image"
            />
            <p className="lightbox-caption">{lightboxPhoto.caption}</p>
          </div>
        </div>
      )}

      {/* Schema.org ImageGallery structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            name: "Benson Dental Studio Gallery",
            description:
              "Photos of our modern dental office and patient smile transformations in Bensonhurst, Brooklyn.",
            url: "https://bensondentalstudio.com/gallery",
            image: officePhotos.map((photo) => ({
              "@type": "ImageObject",
              contentUrl: `https://bensondentalstudio.com${photo.src}`,
              description: photo.alt,
              name: photo.caption,
            })),
          }),
        }}
      />
    </main>
  );
};

export default Gallery;
